import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Card, Color, message, Switch } from '@bb/elements-web';
import { useMutation } from '@tanstack/react-query';
import { updateManagedAccessSettings } from 'api/SupraSystemSubscriptionController';
import { useSupraAccessDetailsPageContext } from '../SupraAccessDetailsPageContext';

const ManagedAccessCard: React.FC = () => {
  const { userId, subscription, silentRefetch } =
    useSupraAccessDetailsPageContext();

  const defaultEnabled = subscription?.singleAccessEnabled ?? false;

  const [singleAccessEnabled, setSingleAccessEnabled] = useState(false);

  useEffect(() => {
    if (subscription) {
      setSingleAccessEnabled(defaultEnabled);
    }
  }, [subscription, defaultEnabled]);

  const mutation = useMutation({
    mutationFn: (checked: boolean) =>
      updateManagedAccessSettings({
        userId,
        singleAccessEnabled: checked,
      }),
    onMutate: (checked: boolean) => {
      const previous = singleAccessEnabled;
      setSingleAccessEnabled(checked);
      return { previous };
    },
    onError: (_error, _checked, context) => {
      message.error('Failed to update Single Access setting.');
      if (context) {
        setSingleAccessEnabled(context.previous);
      }
    },
    onSuccess: () => {
      silentRefetch();
    },
  });

  const handleChange = (checked: boolean) => {
    mutation.mutate(checked);
  };

  return (
    <StyledCard title="Managed Access" dataCy="managed-access-card">
      <Row>
        <RowLabel>Allow user to grant Single Access</RowLabel>
        <Switch
          checked={singleAccessEnabled}
          disabled={mutation.isLoading}
          onChange={handleChange}
          dataCy="managed-access-single-access"
        />
      </Row>
    </StyledCard>
  );
};

export default ManagedAccessCard;

const StyledCard = styled(Card)`
  .ant-card-head-title {
    font-size: 20px;
    font-weight: 600;
    color: ${Color.PRIMARY_LIGHT_TEXT};
  }
  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
    min-height: auto;
  }
  .ant-card-body {
    padding-top: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
`;

const RowLabel = styled.div`
  font-size: 16px;
  color: ${Color.PRIMARY_LIGHT_TEXT};
`;
